import React, { useState } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { Class } from '../../context/types';
import { deleteClass } from '../../services/api';

interface DeleteConfirmationModalProps {
  show: boolean;
  classToDelete: Class | null;
  onClose: () => void;
  onDelete: () => void;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  show,
  classToDelete,
  onClose,
  onDelete,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle delete action
  const handleDelete = async () => {
    if (!classToDelete) return;

    setIsDeleting(true);
    setError(null);

    try {
      await deleteClass(classToDelete.id);

      // Success - call onDelete callback to refresh the list
      onDelete();
      onClose();
    } catch (err: any) {
      console.error('Error deleting class:', err);

      let errorMessage: string;
      if (err.response) {
        // Server responded with error
        if (err.response.status === 403) {
          errorMessage = 'You do not have permission to delete this class.';
        } else if (err.response.status === 404) {
          errorMessage = 'Class not found. It may have already been deleted.';
        } else {
          errorMessage = err.response.data?.detail || 'Failed to delete class.';
        }
      } else if (err.request) {
        // Request made but no response
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Something else happened
        errorMessage = 'Failed to delete class.';
      }

      setError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Reset error when modal closes
  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={!isDeleting}
      centered
      data-bs-theme="dark"
    >
      <Modal.Header closeButton={!isDeleting}>
        <Modal.Title>Confirm Delete</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {classToDelete && (
          <p>
            Are you sure you want to delete the class{' '}
            <strong>"{classToDelete.name}"</strong>?
          </p>
        )}

        <p className="text-muted mb-0">
          This action cannot be undone.
        </p>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isDeleting}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Deleting...
            </>
          ) : (
            'Delete Class'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteConfirmationModal;
